import memoize from "memoizee";
import { v4 as uuidv4 } from "uuid";
import { getBot, createBot } from "@nualang/nualang-api-and-queries/APIs/Bots";
import { memoizeGetUserDetails } from "./users";
import {
  DataType,
  invalidateCache,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import { getImageUrl, getImageUrlWithSize } from "../imageDownload";
import { UploadBotImage } from "../imageUpload";
import config from "../../config";

export const memoizeGetBot = memoize(getBot, { promise: true });

export const formatBot = async (b, username) => {
  const userDetails = await memoizeGetUserDetails(b.createdBy);
  const userImage =
    userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.picture
      : null;
  const verificationStatus =
    userDetails && userDetails.verificationStatus
      ? userDetails.verificationStatus
      : null;
  const gameAward =
    userDetails && userDetails.gameAward ? userDetails.gameAward : null;
  // eslint-disable-next-line camelcase
  const preferred_username =
    userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.preferred_username
      : null;
  const isCreator = b.createdBy === username;
  let pictureXs = null;
  let cardPicture = null;
  if (b.pictureS3Key) {
    pictureXs = getImageUrl(b.pictureS3Key, {
      resize: {
        width: 25,
      },
    });
    cardPicture = getImageUrl(b.pictureS3Key, {
      resize: {
        width: 600,
      },
    });
  } else if (
    b.picture &&
    b.picture.includes("nualang.com/") &&
    b.picture.includes("images")
  ) {
    pictureXs = getImageUrlWithSize(b.picture, null, 25);
    cardPicture = getImageUrlWithSize(b.picture, undefined, 600);
  }

  if (b.picture && b.picture.includes(config.OldImageCreatorURL)) {
    const path = b.picture.split(config.OldImageCreatorURL)[1];
    b.picture = `${config.API}/image-creator${path}`;
  }

  return {
    ...b,
    riveFile: b.riveFile || "",
    userImage,
    verificationStatus,
    gameAward,
    createdByName: preferred_username,
    isCreator,
    pictureXs,
    cardPicture,
  };
};

export const formatBots = (bots = [], username) =>
  Promise.all(bots.map((b) => formatBot(b, username)));

export const formatCompletedBot = async (completion, username) => {
  const { botId } = completion;
  const botResponse = await memoizeGetBot(botId);
  if (botResponse.Item) {
    const formattedBot = await formatBot(botResponse.Item, username);
    return formattedBot;
  }

  return null;
};

export const formatCompletedBots = async (completions = [], username) =>
  Promise.all(completions.map((c) => formatCompletedBot(c, username)));

export const invalidateBotCache = async (bot) => {
  if (bot.courseSectionTopicId.includes("none")) {
    await invalidateCache(DataType.BOT, {
      botId: bot.botId,
      learnLang: bot.learnLang,
    });
  } else {
    const [courseId, sectionId, topicId] = bot.courseSectionTopicId.split("|");
    await invalidateCache(DataType.TOPIC_BOT, {
      courseId,
      sectionId,
      topicId,
      botId: bot.botId,
      learnLang: bot.learnLang,
    });
  }
};

export const duplicateBot = async (values, courseId, sectionId, topicId) => {
  const imageId = uuidv4();
  let pictureBase64String = "";
  let botAvatarBase64String = "";

  const newBotValues = {
    ...values,
    botName: values.botName + " (duplicate)",
    username: "",
    userImage: "",
    isCreator: true,
    visibility: "private",
  };

  delete newBotValues.createdBy;
  delete newBotValues.createdByName;
  delete newBotValues.createdAt;
  delete newBotValues.courseSectionTopicId;
  delete newBotValues.botId;
  delete newBotValues.exerciseCompletionTotal;
  delete newBotValues.exerciseStartTotal;

  if (newBotValues.picture) {
    pictureBase64String = newBotValues.picture.split(",");
  }
  if (newBotValues.botAvatar) {
    botAvatarBase64String = newBotValues.botAvatar.split(",");
  }

  newBotValues.picture = "";
  newBotValues.botAvatar = "";

  const isTopicExercise = courseId && sectionId && topicId ? true : false;
  if (isTopicExercise) {
    newBotValues.courseSectionTopicId = `${courseId}|${sectionId}|${topicId}`;
  }
  if (pictureBase64String) {
    [newBotValues.picture, newBotValues.pictureS3Key] = await UploadBotImage(
      pictureBase64String[1] || pictureBase64String[0],
      `${imageId}-picture`,
    );
  }
  if (botAvatarBase64String) {
    [newBotValues.botAvatar, newBotValues.botAvatarS3Key] =
      await UploadBotImage(
        botAvatarBase64String[1] || botAvatarBase64String[0],
        `${imageId}-botAvatar`,
      );
  }
  const newBot = await createBot(newBotValues, isTopicExercise);
  return newBot;
};
